import React, { ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { useLocalStore } from "@/hooks/use-local-store";
import { useShallow } from "zustand/shallow";
import { apiService } from "@/services/api";
import { DebugModal } from "@/components/debug-modal";
import { useT } from "@/lib/i18n";

const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const t = useT();
  const { userProfile, setUserProfile, setAutoLogin, setPage } = useLocalStore(
    useShallow(({ userProfile, setUserProfile, setAutoLogin, setPage }) => ({
      userProfile,
      setUserProfile,
      setAutoLogin,
      setPage,
    })),
  );
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const onDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      apiService.logout();
      setUserProfile(null);
      setAutoLogin(false);
      setPage("connection"); // Navigate back to the connection page
    } catch (error) {
      console.error("Error during disconnect:", error);
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <DebugModal>
      <div className="flex flex-col h-full w-full bg-background text-foreground">
        <div className="sr-only" id="error-message" />
        <main className="flex flex-col flex-1 w-full overflow-hidden">{children}</main>
        {userProfile ? (
          <footer className="flex justify-between items-center p-3 border-t border-border bg-card shrink-0 gap-2">
            <div className="text-xs text-muted-foreground overflow-hidden flex-1 min-w-0">
              <p className="truncate font-semibold text-foreground select-none" title={userProfile.email}>
                {userProfile.email}
              </p>
              <p className="truncate select-none" title={userProfile.organization || ""}>
                {capitalizeFirstLetter(userProfile.organization || "")}
              </p>
            </div>
            <Button variant="ghost" onClick={onDisconnect} disabled={isDisconnecting} className="shrink-0 select-none">
              {t("connection-disconnect")}
            </Button>
          </footer>
        ) : (
          <footer className="flex justify-center items-center p-3 border-t border-border bg-card shrink-0">
            <div className="text-center text-muted-foreground text-xs">
              <p className="select-none">{t("connection-copyright")}</p>
            </div>
          </footer>
        )}
      </div>
    </DebugModal>
  );
}
